import { useState } from "react";
import { useDispatch } from "react-redux";

import { Alert, IconName, Stack, Tiles } from "@seneca/senekit";

import {
  DEFAULT_MESSAGE,
  ErrorMessage as ErrorMessageType
} from "services/firebase/auth/errorTranslator";
import { ProviderSSO } from "services/firebase/auth/types";

import { linkToParentDashboard } from "features/parents/links";
import useIsAndroidAppRunningInsideWebView from "features/pwa/mobile/android/hooks/useIsAndroidAppRunningInsideWebView";
import { userAccountType } from "features/user";
import { ROUTES } from "routing/routes";

import { ErrorMessage } from "./form";
import { useLocationContext } from "./LocationProvider";
import SSOButton from "./SSOButton";
import makeHandleSSOAuth from "./utils/handleSSOAuth";

const accountType = userAccountType.schoolparent;

export default function SignUpParentSSO() {
  const dispatch = useDispatch();
  const [signInWithRedirect, setSignInWithRedirect] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<
    ProviderSSO | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState<
    ErrorMessageType | undefined
  >();

  const { navigateInContext } = useLocationContext();
  const handleSSOAuth = makeHandleSSOAuth({
    onSignupSuccess: () =>
      navigateInContext(ROUTES.SIGN_UP_PARENT.USER_DETAILS.buildPath({}), {
        replace: true
      }),
    onLoginSuccess: () =>
      navigateInContext(linkToParentDashboard(), {
        replace: true
      }),
    onError: errorMessage => {
      if (errorMessage.message === DEFAULT_MESSAGE) {
        setSignInWithRedirect(true);
        errorMessage.message =
          "There seems to be an issue with displaying the authentication pop-up in your browser. Please try again.";
      }
      setSelectedProvider(undefined);
      setErrorMessage(errorMessage);
    }
  });

  const handleClick = (providerName: ProviderSSO) => {
    setErrorMessage(undefined);
    setSelectedProvider(providerName);
    dispatch(handleSSOAuth({ providerName, signInWithRedirect, accountType }));
  };

  const isAndroidAppInsideWebView = useIsAndroidAppRunningInsideWebView();

  if (isAndroidAppInsideWebView) {
    return (
      <Alert iconName="info">
        Google and Microsoft authentication methods are not supported by this
        browser, please use Chrome browser instead
      </Alert>
    );
  }

  return (
    <Stack space="xlarge">
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      <Tiles columns={[1, 2]} space="xlarge">
        <SSOButton
          iconName={"googleMulticolor" as IconName}
          providerDisplayName="Google"
          onClick={() => handleClick("google")}
          loading={selectedProvider === "google"}
          disabled={selectedProvider && selectedProvider !== "google"}
        />
        <SSOButton
          iconName={"microsoftMulticolor" as IconName}
          providerDisplayName="Microsoft"
          onClick={() => handleClick("microsoft")}
          loading={selectedProvider === "microsoft"}
          disabled={selectedProvider && selectedProvider !== "microsoft"}
        />
      </Tiles>
    </Stack>
  );
}
